'use client'

import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'

import type { HolidayPackage } from '@/src/types/holidays'
import useClevertapForHolidays from '@/src/hooks/analytics/clevertap/holidays'
import useHolidayContacted from '@/src/hooks/holidays/useHolidayContacted'
import { HOLIDAY_ACTION, HOLIDAY_ACTION_TYPE } from '@/src/utils/analytics/holidays/holidayEvents'
import { HOLIDAYS_ENQUIRY_PARAM } from '@/src/utils/holidays'

import EnquiryCardWrapper from '../enquiryCard'

import EnquiryPopup from './enquiryPopup'

/** Long enough for the reader to have started the page, short enough to catch them on it. */
const POPUP_DELAY_MS = 5000

/** One prompt per page per browsing session: a reader who opens three packages is asked on each,
 *  but a page they come back to in the same session stays quiet. */
const SESSION_KEY_PREFIX = 'holidayEnquiryPopupShown:'

/** Namespaces the popup's field ids, and names its lead events apart from the other two forms. */
const POPUP_INSTANCE_ID = 'holiday-enquiry-popup'

/** `placement` on the opened event — the timed panel, as opposed to the phone's docked bar. */
const POPUP_PLACEMENT = 'popup'

interface EnquiryPopupWrapperProps {
    enquiry?: HolidayPackage['enquiry']
    startingPrice?: HolidayPackage['summary']['startingPrice']
    isMobile: boolean
}

const EnquiryPopupWrapper: React.FC<EnquiryPopupWrapperProps> = ({ enquiry, startingPrice, isMobile }) => {
    const t = useTranslations('common')
    const { triggerHolidayUIActionEvent } = useClevertapForHolidays()
    const { isContacted } = useHolidayContacted()
    const searchParams = useSearchParams()
    const [open, setOpen] = useState(false)

    /** Set when the reader arrived by asking to enquire, e.g. a card's Enquire button. */
    const enquirySource = searchParams.get(HOLIDAYS_ENQUIRY_PARAM)

    useEffect(() => {
        // Someone who has already sent an enquiry has nothing to fill in.
        if (isContacted) return undefined

        // Asked for, so it does not wait out the delay and is not suppressed for the session.
        if (enquirySource) {
            setOpen(true)
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.ENQUIRY_OPENED, {
                action_type: HOLIDAY_ACTION_TYPE.FORM,
                form_name: POPUP_INSTANCE_ID,
                placement: POPUP_PLACEMENT,
                lead_source: enquirySource
            })
            return undefined
        }

        const sessionKey = SESSION_KEY_PREFIX + window.location.pathname

        // Private windows and blocked site data throw on access rather than returning null.
        let alreadyShown = false
        try {
            alreadyShown = window.sessionStorage.getItem(sessionKey) === '1'
        } catch {
            alreadyShown = false
        }
        if (alreadyShown) return undefined

        const timer = window.setTimeout(() => {
            try {
                window.sessionStorage.setItem(sessionKey, '1')
            } catch {
                // A page that cannot be marked shows the panel again on a revisit; better than
                // suppressing it for everyone whose site data is blocked.
            }
            setOpen(true)
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.ENQUIRY_OPENED, {
                action_type: HOLIDAY_ACTION_TYPE.FORM,
                form_name: POPUP_INSTANCE_ID,
                placement: POPUP_PLACEMENT,
                delay_ms: POPUP_DELAY_MS
            })
        }, POPUP_DELAY_MS)

        return () => window.clearTimeout(timer)
    }, [isContacted, enquirySource, triggerHolidayUIActionEvent])

    const handleClose = useCallback(() => setOpen(false), [])

    if (!open) return null

    return (
        <EnquiryPopup closeLabel={t('close')} onClose={handleClose} isMobile={isMobile}>
            <EnquiryCardWrapper
                enquiry={enquiry}
                startingPrice={startingPrice}
                instanceId={POPUP_INSTANCE_ID}
                isMobile={isMobile}
            />
        </EnquiryPopup>
    )
}

export { EnquiryPopupWrapper }
export default EnquiryPopupWrapper
